import {
  maybeSelectAdState,
  getCanvasScale,
  makeUpdateDevice,
  updateAdState,
} from 'PreviewReducers/utils';
import { getAdStates, getMetadata } from 'Selectors';

import { getSelectedCustomLayout } from 'PreviewSelectors';
import {
  WIDTH,
  HEIGHT,
  PRESENT,
  PREVIEW,
  LAYOUTS,
  APP,
  UI,
  ADSTATE_CONTAINERS_PATH,
  LAYOUTS_PATH,
} from 'SharedConstants/';
import {
  CUSTOM_BACKGROUND_UPLOADING,
  CUSTOM_PREVIEW_ARTBOARDS_LOADED,
  DEVICE_ORIENTATION,
  ORIENTATIONS,
  FIT_TO_SCREEN,
  CANVAS_SCALE,
  MAX_HEIGHT,
  DIMENSIONS,
  DEVICES,
  BOUNDS,
  DEVICE,
  SELECTED_CONTAINER,
  SELECTED_LAYOUT,
  MOBILE_PORTRAIT_DEVICE_DIMENSIONS,
} from 'PreviewConstants/';
import saveStatuses from 'PreviewConfig/saveStatuses';
import {
  getLeftPositionForAdStateContainer,
  getNextAvailableAdStateContainerKey,
  initializeLayout,
  initializeDuplicatedLayout,
  initializeDuplicatedAdContainer,
  initializeAdContainer,
} from './utils';

export const getDevice = state => state.getIn([UI, DEVICE]);

export const getOrientation = state => state.getIn([UI, DEVICE_ORIENTATION]);

export default function appReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_SELECTED_CUSTOM_LAYOUT_NAME':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        const { name } = action;
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'name'], name);
      });

    case 'UPDATE_SELECTED_CUSTOM_LAYOUT_DEVICE':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        const { device } = action;
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'device'], device);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'UPDATE_LAYOUT_BACKGROUND_OPACITY':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        const { value } = action;
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'backgroundOpacity'], value.opacity);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'UPDATE_CONTAINER':
      return state.withMutations((mutable) => {
        mutable.setIn([UI, CUSTOM_PREVIEW_ARTBOARDS_LOADED], false);
        mutable.setIn([UI, SELECTED_CONTAINER], action.selectedContainer);
      });

    case 'UPDATE_LAYOUT':
      return state.withMutations((mutable) => {
        mutable.setIn([UI, CUSTOM_PREVIEW_ARTBOARDS_LOADED], false);
        mutable.setIn([UI, SELECTED_LAYOUT], action.selectedLayout);
      });

    case 'UPDATE_ADSTATE_CONTAINER_ARTBOARD':
      return state.withMutations((mutable) => {
        const { adContainerKey, selectedArtboard } = action;
        const selectedLayout = getSelectedCustomLayout(state);
        const artboardRecord = getAdStates(state).find(artboard => artboard.name === selectedArtboard);
        const artboardWidth = artboardRecord.viewport.get('width');
        const artboardHeight = artboardRecord.viewport.get('height');
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adContainerKey, 'artboard'], selectedArtboard);
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adContainerKey, 'dimensions', 2], artboardWidth);
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adContainerKey, 'dimensions', 3], artboardHeight);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'UPDATE_ADSTATE_CONTAINER_POSITION':
      return state.withMutations((mutable) => {
        const { adContainerKey, position, value } = action;
        const selectedLayout = getSelectedCustomLayout(state);
        let dimensionMapper;
        if (position === 'left') dimensionMapper = 0;
        if (position === 'top') dimensionMapper = 1;
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adContainerKey, 'dimensions', dimensionMapper], value);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'UPDATE_ADSTATE_CONTAINER_DRAG_POSITION':
      return state.withMutations((mutable) => {
        const { adStateContainerKey, x, y } = action;
        const selectedLayout = getSelectedCustomLayout(state);
        const currentX = mutable.getIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'dimensions', 0]);
        const currentY = mutable.getIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'dimensions', 1]);
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'dimensions', 0], (currentX + x));
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'dimensions', 1], (currentY + y));
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'UPDATE_ADSTATE_CONTAINER_DIMENSIONS':
      return state.withMutations((mutable) => {
        const { adContainerKey, dimension, value } = action;
        let dimensionMapper;
        if (dimension === 'width') dimensionMapper = 2;
        if (dimension === 'height') dimensionMapper = 3;
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adContainerKey, 'dimensions', dimensionMapper], value);
      });

    case 'TOGGLE_ADSTATE_CONTAINER_ISFIXED':
      return state.withMutations((mutable) => {
        const { adStateContainerKey } = action;
        const selectedLayout = getSelectedCustomLayout(state);
        const currentValue = mutable.getIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'isFixed']);
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'isFixed'], !currentValue);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'ADD_NEW_ADSTATE_CONTAINER':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        const incrementedKey = getNextAvailableAdStateContainerKey(state);
        const dimensions = [getLeftPositionForAdStateContainer(), 400, 400, 400];
        const newAdStateContainer = initializeAdContainer({ key: incrementedKey, dimensions });
        const adStateContainerstList = selectedLayout.adStateContainers.concat(newAdStateContainer.key);
        mutable.setIn([APP, PRESENT, PREVIEW, LAYOUTS, selectedLayout.key, 'adStateContainers'], adStateContainerstList);
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, newAdStateContainer.key], newAdStateContainer);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'DELETE_ADSTATE_CONTAINER':
      return state.withMutations((mutable) => {
        const { adStateContainerKey } = action;
        const selectedLayout = getSelectedCustomLayout(state);
        const adStateContainerstList = selectedLayout.adStateContainers.filter(adStateContainer => adStateContainer !== adStateContainerKey);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'adStateContainers'], adStateContainerstList);
        mutable.deleteIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey]);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'SAVE_COMPLETED':
      return state.setIn([UI, 'saveStatus'], saveStatuses.saveReady);

    case 'SAVE_ERROR':
      return state.setIn([UI, 'saveStatus'], saveStatuses.saveError);

    case 'SAVE_SUCCESS':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], false);
        mutable.setIn([UI, 'saveStatus'], saveStatuses.saveSuccess);
      });

    case 'SAVING_STILL':
      return state.setIn([UI, 'saveStatus'], saveStatuses.savingStill);

    case 'SAVING':
      return state.setIn([UI, 'saveStatus'], saveStatuses.saving);

    case 'CREATE_LAYOUT':
      return state.withMutations((mutable) => {
        const { key, id, name, background, backgroundFileName, backgroundDimensions } = action;

        const newLayoutData = {
          key,
          id,
          name,
          background,
          backgroundFileName,
          backgroundDimensions,
        };

        const newLayout = initializeLayout(newLayoutData);
        mutable.setIn([...LAYOUTS_PATH, newLayout.key], newLayout);
        mutable.setIn([UI, SELECTED_LAYOUT], newLayout.key);
      });

    case 'LOAD_DUPLICATED_LAYOUT':
      return state.withMutations((mutable) => {
        const { incrementedKey, layout } = action;
        layout.key = incrementedKey;

        const duplicatedLayout = initializeDuplicatedLayout(incrementedKey, layout);
        mutable.setIn([...LAYOUTS_PATH, incrementedKey], duplicatedLayout);

        Object.keys(layout.adStateContainers).forEach((containerIndex) => {
          const container = layout.adStateContainers[containerIndex];
          const incrementedContainerKey = getNextAvailableAdStateContainerKey(mutable);
          const dimensions = container.dimensions;
          const artboard = container.artboard;
          const isFixed = container.isFixed;
          const newAdStateContainer = initializeDuplicatedAdContainer({ key: incrementedContainerKey, dimensions, artboard, isFixed });
          mutable.setIn([...ADSTATE_CONTAINERS_PATH, newAdStateContainer.key], newAdStateContainer);
          const adStateContainers = mutable.getIn([...LAYOUTS_PATH, duplicatedLayout.key, 'adStateContainers']);
          const adStateContainerstList = adStateContainers.concat(newAdStateContainer.key);
          mutable.setIn([APP, PRESENT, PREVIEW, LAYOUTS, duplicatedLayout.key, 'adStateContainers'], adStateContainerstList);
        });

        mutable.setIn([UI, SELECTED_LAYOUT], incrementedKey);
      });

    case 'DELETE_LAYOUT':
      return state.withMutations((mutable) => {
        const { layout } = action;
        layout.adStateContainers.forEach((container) => {
          mutable.deleteIn([...ADSTATE_CONTAINERS_PATH, container]);
        });
        mutable.deleteIn([...LAYOUTS_PATH, layout.key]);
      });

    case 'UPDATE_LAYOUT_BACKGROUND':
      return state.withMutations((mutable) => {
        const { layout, background, backgroundFileName, backgroundDimensions } = action;
        const dimensions = new Map([[0, backgroundDimensions[0]], [1, backgroundDimensions[1]]]);
        const device = (backgroundDimensions[0] <= MOBILE_PORTRAIT_DEVICE_DIMENSIONS[0]) ? 'mobile-portrait' : 'desktop';
        mutable.setIn([...LAYOUTS_PATH, layout.key, 'device'], device);
        mutable.setIn([...LAYOUTS_PATH, layout.key, 'background'], background);
        mutable.setIn([...LAYOUTS_PATH, layout.key, 'backgroundFileName'], backgroundFileName);
        mutable.setIn([...LAYOUTS_PATH, layout.key, 'backgroundDimensions'], dimensions);
        mutable.setIn([UI, CUSTOM_BACKGROUND_UPLOADING], false);
      });

    case 'TOGGLE_LAYOUT_IN_APP':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        const currentValue = mutable.getIn([...LAYOUTS_PATH, selectedLayout.key, 'inApp']);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'inApp'], !currentValue);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'UPDATE_LAYOUT_DEVICE_HEADER':
      return state.withMutations((mutable) => {
        const { value } = action;
        const selectedLayout = getSelectedCustomLayout(state);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'deviceHeader'], value.header);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'TOGGLE_LAYOUT_BACKGROUND_MONOCHROME':
      return state.withMutations((mutable) => {
        const selectedLayout = getSelectedCustomLayout(state);
        const currentValue = mutable.getIn([...LAYOUTS_PATH, selectedLayout.key, 'backgroundMonochrome']);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'backgroundMonochrome'], !currentValue);
        mutable.setIn([...LAYOUTS_PATH, selectedLayout.key, 'hasChanged'], true);
      });

    case 'SET_BACKGROUND_UPLOADING': {
      return state.withMutations((mutable) => {
        mutable.setIn([UI, CUSTOM_BACKGROUND_UPLOADING], true);
      });
    }

    case 'UPDATE_ARTBOARD': {
      const { artboardKey } = action;
      return state.withMutations((mutable) => {
        const newActiveAdstate = updateAdState(mutable, artboardKey);
        if (newActiveAdstate) {
          mutable.setIn([UI, DIMENSIONS], newActiveAdstate.viewport);
          if (!mutable.getIn([UI, FIT_TO_SCREEN])) return;
          const newCanvasScale = getCanvasScale(mutable);
          mutable.setIn([UI, CANVAS_SCALE], newCanvasScale);
        }
      });
    }

    case 'SELECT_ADSTATE_CONTAINER': {
      const { adStateContainerKey } = action;
      return state.withMutations((mutable) => {
        const selectedAdStateContainers = mutable.getIn([...ADSTATE_CONTAINERS_PATH]).filter(c => c.selected);
        selectedAdStateContainers.forEach((adStateContainer) => {
          mutable.setIn([...ADSTATE_CONTAINERS_PATH, adStateContainer.key, 'selected'], false);
        });
        mutable.setIn([...ADSTATE_CONTAINERS_PATH, adStateContainerKey, 'selected'], true);
      });
    }

    case 'DESELECT_ADSTATE_CONTAINER': {
      return state.withMutations((mutable) => {
        const selectedAdStateContainers = mutable.getIn([...ADSTATE_CONTAINERS_PATH]).filter(c => c.selected);
        selectedAdStateContainers.forEach((adStateContainer) => {
          mutable.setIn([...ADSTATE_CONTAINERS_PATH, adStateContainer.key, 'selected'], false);
        });
      });
    }

    case 'UPDATE_DIMENSIONS': {
      const { [DIMENSIONS]: newDimensions } = action;
      const maintainAspectRatio = getMetadata(state).get('maintainAspectRatio');
      const activeArtboard = store.getState().activeArtboard;
      const dimensions = state.getIn([UI, DIMENSIONS]).merge(newDimensions);
      const height = dimensions.get(HEIGHT);
      const maxHeight = state.getIn([UI, BOUNDS, MAX_HEIGHT]);

      return state.withMutations((mutable) => {
        const newActiveAdstate = maybeSelectAdState(mutable, dimensions);
        if (!newActiveAdstate) {
          const delta = newDimensions.hasOwnProperty('width') ? WIDTH : HEIGHT;
          const inverseDelta = delta === WIDTH ? HEIGHT : WIDTH;
          const inverseScale = dimensions.get(delta) / activeArtboard.viewport[delta];
          const inverseValue = activeArtboard.viewport[inverseDelta] * inverseScale;
          const adjustedDimensions = dimensions.merge({ [inverseDelta]: inverseValue });
          const updatedDimensions = maintainAspectRatio ? adjustedDimensions : dimensions;
          mutable.setIn([UI, DIMENSIONS], updatedDimensions);
          return;
        }
        if (!mutable.getIn([UI, FIT_TO_SCREEN])) return;

        const activeArtboardHeight = newActiveAdstate.viewport.get(HEIGHT);
        const canvasScale = getCanvasScale(mutable);

        mutable.setIn([UI, CANVAS_SCALE], canvasScale);

        const creativeScale = dimensions.get(WIDTH) / newActiveAdstate.viewport.get(WIDTH);

        let adjustedDimensions = dimensions.merge({ [WIDTH]: newActiveAdstate.viewport.get(WIDTH) * creativeScale });
        adjustedDimensions = dimensions.merge({ [HEIGHT]: newActiveAdstate.viewport.get(HEIGHT) * creativeScale });
        mutable.setIn(
          [UI, DIMENSIONS],
          mutable.getIn([UI, DIMENSIONS]).merge(adjustedDimensions),
        );

        const isCurrentHeightAtNewScaleTooBig = height * canvasScale > maxHeight;
        if (isCurrentHeightAtNewScaleTooBig) {
          adjustedDimensions = dimensions.merge({ [HEIGHT]: activeArtboardHeight });
          mutable.setIn(
            [UI, DIMENSIONS],
            mutable.getIn([UI, DIMENSIONS]).merge(adjustedDimensions),
          );
        }
      });
    }

    case 'CUSTOM_PREVIEW_ARTBOARDS_LOADED': {
      return state.setIn([UI, CUSTOM_PREVIEW_ARTBOARDS_LOADED], true);
    }

    case 'UPDATE_CANVAS_SCALE': {
      return state.setIn([UI, CANVAS_SCALE], action.scale);
    }

    case 'TOGGLE_FIT_TO_SCREEN': {
      const willFitToScreen = !state.getIn([UI, FIT_TO_SCREEN]);
      const newCanvasScale = willFitToScreen ? getCanvasScale(state) : 1;

      return state.withMutations((mutable) => {
        mutable.setIn([UI, CANVAS_SCALE], newCanvasScale);
        mutable.setIn([UI, FIT_TO_SCREEN], willFitToScreen);
      });
    }

    case 'UPDATE_DEVICE': {
      const device = DEVICES[action.deviceKey] || null;
      const orientation = getOrientation(state);
      return state.withMutations(makeUpdateDevice(device, orientation));
    }

    case 'TOGGLE_DEVICE_ORIENTATION': {
      const device = getDevice(state);
      const currentOrientation = getOrientation(state);
      const nextOrientation = ORIENTATIONS.find(orientation => orientation !== currentOrientation);
      return state.withMutations(makeUpdateDevice(device, nextOrientation));
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Preview/reducers/index.js